import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, MessageCircle, Share2, Music, Play, Volume2, VolumeX } from 'lucide-react';

/**
 * FeedView - TikTok-style vertical scrolling feed
 *
 * @param {array} feed - Array of feed items from DataSeeder
 */
export default function FeedView({ feed = [] }) {
  // Show loading state if feed is empty
  if (!feed || feed.length === 0) {
    return (
      <div className="h-[calc(100vh-64px)] w-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <motion.div
            className="w-16 h-16 border-4 border-gjyl-accent border-t-transparent rounded-full mx-auto mb-4"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          />
          <p className="text-gray-400 text-sm">Loading feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-[calc(100vh-64px)] w-screen overflow-y-scroll overflow-x-hidden snap-y snap-mandatory bg-black"
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {feed.map((post) => (
        <FeedItem key={post.id} post={post} />
      ))}
    </div>
  );
}

/**
 * FeedItem - Individual feed post with video/image support
 */
function FeedItem({ post }) {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes);
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const lastTap = useRef(0);
  const videoRef = useRef(null);

  // Handle double-tap to like
  const handleDoubleTap = (e) => {
    // Prevent if tapping on action buttons
    if (e.target.closest('button')) return;

    const now = Date.now();
    const timeSinceLastTap = now - lastTap.current;

    if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
      // Double tap detected
      if (!liked) {
        setLiked(true);
        setLikeCount(likeCount + 1);
      }
      setShowHeartAnimation(true);
      setTimeout(() => setShowHeartAnimation(false), 800);
    } else {
      // Single tap - toggle video play/pause
      if (videoRef.current && videoLoaded) {
        if (videoRef.current.paused) {
          videoRef.current.play();
          setIsPlaying(true);
        } else {
          videoRef.current.pause();
          setIsPlaying(false);
        }
      }
    }

    lastTap.current = now;
  };

  // Handle heart icon click
  const handleLikeClick = () => {
    if (liked) {
      setLiked(false);
      setLikeCount(likeCount - 1);
    } else {
      setLiked(true);
      setLikeCount(likeCount + 1);
    }
  };

  // Toggle video mute
  const handleToggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  // Check if post has actual media
  const hasVideo = post.videoUrl && !videoError;
  const hasThumbnail = post.thumbnail;

  return (
    <div
      className="relative h-[calc(100vh-64px)] w-screen snap-start snap-always flex-shrink-0"
      onClick={handleDoubleTap}
    >
      {/* Actual Video (if available) */}
      {hasVideo && (
        <video
          ref={videoRef}
          src={post.videoUrl}
          poster={hasThumbnail ? post.thumbnail : undefined}
          className="absolute inset-0 w-full h-full object-cover"
          loop
          muted={isMuted}
          playsInline
          autoPlay
          onLoadedData={() => {
            setVideoLoaded(true);
            setIsPlaying(true);
          }}
          onError={() => setVideoError(true)}
        />
      )}

      {/* Thumbnail/Image (if no video or video error) */}
      {!hasVideo && hasThumbnail && (
        <img
          src={post.thumbnail}
          alt={post.caption}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Fallback gradient (only if no video or thumbnail) */}
      {!hasVideo && !hasThumbnail && (
        <div className={`absolute inset-0 bg-gradient-to-b ${post.gradient}`}>
          {/* Play indicator (centered) */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0.6 }}
            animate={{ opacity: [0.6, 0.9, 0.6] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          >
            <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
              <Play className="w-10 h-10 text-white/80 ml-1" fill="white" strokeWidth={0} />
            </div>
          </motion.div>

          {/* Loading shimmer effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
            animate={{
              x: ['-100%', '100%']
            }}
            transition={{
              repeat: Infinity,
              duration: 3,
              ease: 'linear'
            }}
          />
        </div>
      )}

      {/* Video loading overlay */}
      {hasVideo && !videoLoaded && (
        <div className={`absolute inset-0 bg-gradient-to-b ${post.gradient} flex items-center justify-center`}>
          <motion.div
            className="w-12 h-12 border-3 border-white/30 border-t-white rounded-full"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          />
        </div>
      )}

      {/* Mute button (for videos) */}
      {hasVideo && videoLoaded && (
        <button
          onClick={handleToggleMute}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center z-20"
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5 text-white" />
          ) : (
            <Volume2 className="w-5 h-5 text-white" />
          )}
        </button>
      )}

      {/* Play/Pause indicator */}
      {hasVideo && videoLoaded && !isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="w-20 h-20 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-10 h-10 text-white ml-1" fill="white" strokeWidth={0} />
          </div>
        </div>
      )}

      {/* Double-tap heart animation */}
      <AnimatePresence>
        {showHeartAnimation && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.2, 1], opacity: [0, 1, 0] }}
            exit={{ scale: 1.3, opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <Heart className="w-32 h-32 text-white" fill="#ef4444" strokeWidth={0} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Right side action column */}
      <div className="absolute right-3 bottom-32 flex flex-col items-center space-y-6 z-10">
        {/* User avatar */}
        <motion.div
          className="relative"
          whileTap={{ scale: 0.95 }}
        >
          <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${post.gradient} flex items-center justify-center text-white font-bold text-lg border-2 border-white`}>
            {post.avatar ? (
              <img src={post.avatar} alt={post.username} className="w-full h-full rounded-full object-cover" />
            ) : (
              post.username.charAt(0).toUpperCase()
            )}
          </div>
          {/* Follow button */}
          <motion.div
            className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-gjyl-accent flex items-center justify-center"
            whileTap={{ scale: 0.9 }}
          >
            <span className="text-white text-lg font-bold leading-none">+</span>
          </motion.div>
        </motion.div>

        {/* Like button */}
        <ActionButton
          icon={Heart}
          count={formatCount(likeCount)}
          active={liked}
          onClick={handleLikeClick}
        />

        {/* Comment button */}
        <ActionButton
          icon={MessageCircle}
          count={formatCount(post.comments)}
        />

        {/* Share button */}
        <ActionButton
          icon={Share2}
          count={formatCount(post.shares)}
        />
      </div>

      {/* Bottom overlay - User info */}
      <div className="absolute bottom-0 left-0 right-0 p-4 pb-24 z-10 bg-gradient-to-t from-black/60 via-black/30 to-transparent">
        {/* Username */}
        <motion.h3
          className="text-white font-semibold text-base mb-2"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          @{post.username}
        </motion.h3>

        {/* Caption */}
        <motion.p
          className="text-white text-sm mb-3 line-clamp-2"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          {post.caption}
        </motion.p>

        {/* Music ticker */}
        <motion.div
          className="flex items-center space-x-2 overflow-hidden"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Music className="w-4 h-4 text-white flex-shrink-0" />
          <motion.div
            className="whitespace-nowrap text-white text-xs"
            animate={{
              x: [0, -200]
            }}
            transition={{
              repeat: Infinity,
              duration: 10,
              ease: 'linear'
            }}
          >
            {post.music} • {post.music} • {post.music}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

/**
 * ActionButton - Side action button component
 */
function ActionButton({ icon: Icon, count, active = false, onClick }) {
  return (
    <motion.button
      className="flex flex-col items-center space-y-1"
      whileTap={{ scale: 0.85 }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      <motion.div
        className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center"
        animate={active ? {
          scale: [1, 1.2, 1],
        } : {}}
        transition={{ duration: 0.3 }}
      >
        <Icon
          className={`w-7 h-7 ${active ? 'text-red-500' : 'text-white'}`}
          fill={active ? '#ef4444' : 'none'}
          strokeWidth={2}
        />
      </motion.div>
      {count && (
        <span className="text-white text-xs font-semibold drop-shadow-lg">
          {count}
        </span>
      )}
    </motion.button>
  );
}

/**
 * Format large numbers for display
 */
function formatCount(count) {
  if (count < 1000) return count.toString();
  if (count < 10000) return `${(count / 1000).toFixed(1)}K`;
  if (count < 1000000) return `${Math.floor(count / 1000)}K`;
  return `${(count / 1000000).toFixed(1)}M`;
}
